import { Loader2, Tag } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import OffersShowcaseSection from "../components/OffersShowcaseSection";
import { SEO } from "../components/SEO";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

export const OffersPage = () => {
  const { t } = useLanguage();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOffers = async () => {
      setLoading(true);
      setLoadFailed(false);

      try {
        const nextOffers = await odooApi.getOffers();
        if (!isMounted) {
          return;
        }
        setOffers(nextOffers);
      } catch (error) {
        console.error("Failed to load offers page:", error);
        if (!isMounted) {
          return;
        }
        setOffers([]);
        setLoadFailed(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOffers();

    return () => {
      isMounted = false;
    };
  }, []);

  const enabledOffers = useMemo(
    () => offers.filter((offer) => offer?.enabled !== false),
    [offers],
  );

  const totalCars = useMemo(
    () => enabledOffers.reduce((sum, offer) => sum + (offer.cars?.length || 0), 0),
    [enabledOffers],
  );

  return (
    <>
      <SEO
        title={t("offers.pageTitle", "Offers | Velaris Royal Car Rental")}
        description={t("offers.pageDescription", "Browse all active luxury car rental offers, promotions, and limited-time deals from Velaris Royal.")}
        keywords="luxury car rental offers, dubai car rental deals, monthly rental offers, velaris offers"
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("nav.offers", "Offer") },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="offers-page">
        <section className="relative overflow-hidden bg-black py-20 md:py-28">
          <div className="absolute inset-0 opacity-25">
            <img
              src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&h=900&fit=crop"
              alt={t("about.heroAlt", "Luxury cars")}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(212,175,55,0.25),_transparent_24%),linear-gradient(180deg,rgba(0,0,0,0.35),rgba(0,0,0,0.82))]" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37]">
                <Tag className="w-4 h-4" />
                {t("offers.heroEyebrow", "Offer")}
              </span>
              <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl lg:text-6xl font-bold text-white">
                {t("offers.heroTitle", "All Current Offers")}
              </h1>
              <p className="mt-5 max-w-2xl text-base sm:text-lg text-white/70">
                {t("offers.heroDescription", "Explore every active campaign, compare limited-time deals, and jump directly into discounted luxury rentals.")}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-white/45">
                  {t("offers.totalOffers", "Active Offers")}
                </p>
                <p className="mt-2 text-3xl font-bold text-white">{enabledOffers.length}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-white/45">
                  {t("offers.totalCars", "Discounted Cars")}
                </p>
                <p className="mt-2 text-3xl font-bold text-white">{totalCars}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-white/45">
                  {t("offers.browseFleet", "Need More Options?")}
                </p>
                <Link
                  to="/cars"
                  className="mt-2 inline-flex text-lg font-semibold text-[#FFD700] transition-colors hover:text-white"
                >
                  {t("btn.viewAllCars", "View All Cars")}
                </Link>
              </div>
            </div>
          </div>
        </section>

        {loading && (
          <section className="py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-3 text-gray-500 dark:text-gray-300">
              <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
              <span>{t("offers.loading", "Loading offers...")}</span>
            </div>
          </section>
        )}

        {!loading && loadFailed && (
          <section className="py-12 bg-white dark:bg-gray-950">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="rounded-2xl border border-red-200 bg-red-50 px-6 py-5 text-center text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
                {t("offers.loadError", "The offers page could not be loaded right now.")}
              </div>
            </div>
          </section>
        )}

        {!loading && !loadFailed && (
          <OffersShowcaseSection
            offers={enabledOffers}
            className="py-14 md:py-20"
            title={t("offers.sectionTitle", "All Offers")}
            subtitle={t("offers.sectionSubtitle", "Available Now")}
            emptyState={(
              <section className="py-16 bg-white dark:bg-gray-950">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="rounded-[2rem] border border-dashed border-[#D4AF37]/30 bg-amber-50/60 px-6 py-12 text-center dark:bg-[#D4AF37]/5">
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">
                      {t("offers.emptyEyebrow", "Offer")}
                    </p>
                    <h2 className="mt-3 font-[var(--font-display)] text-3xl font-bold text-gray-900 dark:text-white">
                      {t("offers.emptyTitle", "No offers available right now")}
                    </h2>
                    <p className="mt-3 text-gray-600 dark:text-gray-400">
                      {t("offers.emptyDescription", "Check again soon or browse the full fleet for the latest luxury rentals.")}
                    </p>
                    <Link
                      to="/cars"
                      className="mt-6 inline-flex rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-black transition-colors hover:bg-[#FFD700]"
                    >
                      {t("btn.browse", "Browse Cars")}
                    </Link>
                  </div>
                </div>
              </section>
            )}
          />
        )}
      </div>
    </>
  );
};

export default OffersPage;
